import { RootState } from '../../app/store';
import { createSlice, PayloadAction, current } from '@reduxjs/toolkit';

const initialGranularEntitlementState = {
    planFeatures: [] as any,
    deviceTypeIds: [] as any,
    activeFeature: 'NameAndDesc',
    entitlementInput: {
        name: '',
        description: '',
        activeServiceContract: false,
        basePlan: false,
        reveneraSupported: false,
        partNumber: null,
        hasDuration: false,
        durationMonths: 3 as number | null,
        hasDeviceLimit: false,
        deviceLimit: 50 as number | null,
        hasTrialFeaturePackage: false,
        trialFeaturePackageId: null as string | null,
    },
    cancelConfirmModalOpen: false,
    shouldCancelConfirmModalOpen: false,
    hydrated: false,

    conflictModalOpen: false,
    conflictModalRecords: [] as any[],
    trialAddedDeviceTypeIds: [] as string[],
};

const granularEntitlementSlice = createSlice({
    name: 'granularEntitlement',
    initialState: initialGranularEntitlementState,
    reducers: {
        setActiveFeature: (state, action: PayloadAction<any>) => {
            state.activeFeature = action.payload;
        },
        setShouldCancelConfirmModalOpen: (state, action: PayloadAction<boolean>) => {
            state.shouldCancelConfirmModalOpen = action.payload;
        },
        setCancelConfirmModalOpen: (state, action: PayloadAction<boolean>) => {
            state.cancelConfirmModalOpen = action.payload;
        },
        setEntitlementInput: (state, action: PayloadAction<any>) => {
            state.entitlementInput = action.payload;
        },
        setPlanfeatures: (state, action: PayloadAction<any>) => {
            state.planFeatures = action.payload;
        },
        setDeviceTypeIds: (state, action: PayloadAction<any>) => {
            if (Object?.keys(action?.payload ?? {})?.length) {
                const deviceTypeIndex: any = current(state).deviceTypeIds?.findIndex((deviceTypeId: any) => {
                    return action?.payload?.id === deviceTypeId?.id;
                });
                if (deviceTypeIndex === -1) {
                    state.deviceTypeIds.push(action?.payload);
                } else {
                    state.deviceTypeIds.splice(deviceTypeIndex, 1);
                }
            } else {
                state.deviceTypeIds = action.payload;
            }
        },
        setFeatureIds: (state, action: PayloadAction<any>) => {
            const filterByDependsOnId = (data: any[]): any[] => {
                const validIds = new Set(data?.map((item) => item?.id));
                return data.filter((item) => {
                    const dependsOnId = item?.dependsOn?.id;
                    if (dependsOnId && !validIds.has(dependsOnId)) {
                        validIds.delete(item?.id);
                        return false;
                    }
                    return true;
                });
            };

            const setIds = (selectedItem: any): void => {
                const planFeatures = current(state).planFeatures;
                const index = planFeatures.findIndex((item: any) => item?.id === selectedItem?.id);

                if (index === -1 && !action.payload?.isRemoved) {
                    state.planFeatures.push(selectedItem);
                } else if (index !== -1) {
                    state.planFeatures.splice(index, 1);
                }
            };

            action?.payload?.ids?.forEach(setIds);

            const filtered = filterByDependsOnId(current(state).planFeatures);
            state.planFeatures = filtered;
        },

        setDeviceTypeIdsDefault: (state, action: PayloadAction<any>) => {
            state.deviceTypeIds = action.payload?.filter(
                (value: any, index: number, self: any) => index === self.findIndex((t: any) => t.id === value.id)
            );
        },
        setDeviceTypeCountToggle: (state, action: PayloadAction<{ id: string; value: boolean }>) => {
            const { id, value } = action.payload ?? {};
            const index = state.deviceTypeIds?.findIndex((dt: any) => dt?.id === id);
            if (index >= 0) {
                state.deviceTypeIds[index] = {
                    ...state.deviceTypeIds[index],
                    skipLicense: value,
                };
            }
        },
        setHydrated: (state, action: PayloadAction<boolean>) => {
            state.hydrated = action.payload;
        },
        setTrialAddedDeviceTypeIds: (state, action: PayloadAction<string[]>) => {
            state.trialAddedDeviceTypeIds = action.payload;
        },
        clearTrialAddedDeviceTypes: (state) => {
            const toRemove = new Set(state.trialAddedDeviceTypeIds);
            state.deviceTypeIds = state.deviceTypeIds.filter((dt: any) => !toRemove.has(dt?.id));
            state.trialAddedDeviceTypeIds = [];
        },
        setResetGranularEntitlement: () => {
            return initialGranularEntitlementState;
        },

        openConflictModal: (state, action: PayloadAction<any[]>) => {
            state.conflictModalOpen = true;
            state.conflictModalRecords = action.payload ?? [];
        },
        closeConflictModal: (state) => {
            state.conflictModalOpen = false;
            state.conflictModalRecords = [];
        },
    },
});

export const {
    setActiveFeature,
    setShouldCancelConfirmModalOpen,
    setCancelConfirmModalOpen,
    setEntitlementInput,
    setPlanfeatures,
    setDeviceTypeIds,
    setDeviceTypeIdsDefault,
    setDeviceTypeCountToggle,
    setFeatureIds,
    setHydrated,
    setResetGranularEntitlement,
    openConflictModal,
    closeConflictModal,
    setTrialAddedDeviceTypeIds,
    clearTrialAddedDeviceTypes,
} = granularEntitlementSlice.actions;
export const selectEntitlementInput = (state: RootState): any => state.granularEntitlement.entitlementInput;
export const selectPlanFeatures = (state: RootState): any => state.granularEntitlement.planFeatures;
export const selectDeviceTypeIds = (state: RootState): any => state.granularEntitlement.deviceTypeIds;
export const selectActiveFeature = (state: RootState): any => state.granularEntitlement.activeFeature;

export const selectHydrated = (state: RootState): boolean => state.granularEntitlement.hydrated;
export const selectConflictModalOpen = (state: RootState): any => state.granularEntitlement.conflictModalOpen;
export const selectConflictModalRecords = (state: RootState): any => state.granularEntitlement.conflictModalRecords;
export const selectTrialAddedDeviceTypeIds = (state: RootState): string[] =>
    state.granularEntitlement.trialAddedDeviceTypeIds;

export default granularEntitlementSlice;
